import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"; // ✅ FIXED import
import "./Overview.css";

export default function Overview() {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState({});

  useEffect(() => {
    const students = JSON.parse(localStorage.getItem("students")) || [];

    // 🔥 Group by course
    const grouped = {};
    students.forEach((stu) => {
      const courseKey = `${stu.departmentName}_${stu.course?.code}_Y${stu.course?.year}_S${stu.course?.semester}_B${stu.course?.batch}`;
      if (!grouped[courseKey]) {
        grouped[courseKey] = {
          key: courseKey,
          dept: stu.departmentName,
          code: stu.course?.code,
          year: stu.course?.year,
          sem: stu.course?.semester,
          batch: stu.course?.batch,
          students: [],
        };
      }
      grouped[courseKey].students.push(stu);
    });

    setCourses(Object.values(grouped));

    // 🔥 Load attendance
    const allRecords = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith("attendance_")) {
        try {
          const record = JSON.parse(localStorage.getItem(key));
          if (record?.subject) {
            if (!allRecords[record.subject]) allRecords[record.subject] = [];
            allRecords[record.subject].push(record);
          }
        } catch (e) {
          console.error("Error parsing attendance", e);
        }
      }
    }
    setAttendanceRecords(allRecords);
  }, []);

  // ✅ Back navigation
  const resetView = () => {
    setSelectedCourse(null);
    setSelectedSubject(null);
  };

  // ✅ Step 1: Show all courses
  if (!selectedCourse) {
    return (
      <div className="overview-container">
        <h1 className="page-title">📚 View Attendance</h1>
        <ul className="course-list">
          {courses.map((c) => (
            <li
              key={c.key}
              className="course-item"
              onClick={() => setSelectedCourse(c)}
            >
              {c.code} ({c.dept} | Y{c.year} | S{c.sem} | B{c.batch})
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // ✅ Step 2: Show subjects under course
  if (selectedCourse && !selectedSubject) {
    const subjects = Object.keys(attendanceRecords).filter((subj) =>
      attendanceRecords[subj].some(
        (rec) =>
          rec.dept === selectedCourse.dept &&
          rec.year === selectedCourse.year &&
          rec.semester === selectedCourse.sem &&
          rec.batch === selectedCourse.batch
      )
    );

    return (
      <div className="overview-container">
        <button className="back-btn" onClick={resetView}>
          ⬅ Back to Courses
        </button>
        <h1 className="page-title">
          {selectedCourse.code} ({selectedCourse.dept} | Y{selectedCourse.year} | S
          {selectedCourse.sem} | B{selectedCourse.batch})
        </h1>
        <h2 className="section-title">Subjects</h2>
        {subjects.length === 0 ? (
          <p>No subjects found yet.</p>
        ) : (
          <ul className="subject-list">
            {subjects.map((subj) => (
              <li
                key={subj}
                className="subject-item"
                onClick={() => setSelectedSubject(subj)}
              >
                {subj}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // ✅ Step 3: Show student records under subject
  if (selectedCourse && selectedSubject) {
    const courseRecords =
      attendanceRecords[selectedSubject]?.filter(
        (rec) =>
          rec.dept === selectedCourse.dept &&
          rec.year === selectedCourse.year &&
          rec.semester === selectedCourse.sem &&
          rec.batch === selectedCourse.batch
      ) || [];

    const dates = [...new Set(courseRecords.map((r) => r.date))];

    const tableData = selectedCourse.students.map((stu) => {
      let presentCount = 0;
      const marks = {};

      dates.forEach((d) => {
        const sessionsForDate = courseRecords.filter((r) => r.date === d);
        const isPresent = sessionsForDate.some((s) =>
          s.present.includes(stu.id)
        );
        if (isPresent) {
          marks[d] = "P";
          presentCount++;
        } else {
          marks[d] = "AB";
        }
      });

      const percentage =
        dates.length > 0 ? Math.round((presentCount / dates.length) * 100) : 0;

      return { ...stu, percentage, marks };
    });

    // 📊 Export Excel
    const exportExcel = () => {
      const worksheet = XLSX.utils.json_to_sheet(
        tableData.map((stu) => ({
          ID: stu.id,
          Name: stu.name,
          Percentage: stu.percentage + "%",
          ...stu.marks,
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance");
      XLSX.writeFile(
        workbook,
        `${selectedSubject}_Y${selectedCourse.year}_S${selectedCourse.sem}.xlsx`
      );
    };

    // 📑 Export PDF
    const exportPDF = () => {
      const doc = new jsPDF();
      doc.text(`${selectedSubject} Attendance Report`, 14, 10);

      autoTable(doc, {
        startY: 20,
        head: [["Student ID", "Name", "Total %", ...dates]],
        body: tableData.map((stu) => [
          stu.id,
          stu.name,
          stu.percentage + "%",
          ...dates.map((d) => stu.marks[d]),
        ]),
        didParseCell: (data) => {
          if (data.section === "body" && (data.cell.raw === "P" || data.cell.raw === "AB")) {
            data.cell.styles.textColor = data.cell.raw === "P" ? "green" : "red";
          }
        },
      });

      doc.save(
        `${selectedSubject}_Y${selectedCourse.year}_S${selectedCourse.sem}.pdf`
      );
    };

    return (
      <div className="overview-container">
        <button className="back-btn" onClick={() => setSelectedSubject(null)}>
          ⬅ Back to Subjects
        </button>
        <h1 className="page-title">
          {selectedSubject} ({selectedCourse.dept} | Y{selectedCourse.year} | S
          {selectedCourse.sem} | B{selectedCourse.batch})
        </h1>

        <table className="styled-table">
          <thead>
            <tr>
              <th>Student ID</th>
              <th>Name</th>
              <th>Total %</th>
              {dates.map((d, i) => (
                <th key={i}>{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((stu) => (
              <tr key={stu.id}>
                <td>{stu.id}</td>
                <td>{stu.name}</td>
                <td>{stu.percentage}%</td>
                {dates.map((d, i) => (
                  <td
                    key={i}
                    className={
                      stu.marks[d] === "P" ? "status-present" : "status-absent"
                    }
                  >
                    {stu.marks[d]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="export-buttons">
          <button className="btn-excel" onClick={exportExcel}>
            📊 Export Excel
          </button>
          <button className="btn-pdf" onClick={exportPDF}>
            📑 Export PDF
          </button>
        </div>
      </div>
    );
  }
}
